/**
 * Image Optimization Utilities
 * Helpers for responsive images and optimal loading
 * 
 * NOTE: Vercel Blob does NOT support on-the-fly image transformation.
 * Images are pre-optimized on the server using sharp before upload.
 * - Product images: full (1200px) + thumbnail (400px)
 * - Avatars: max 400px
 * - Progress photos: max 1200px
 */

/**
 * Check if URL is from Vercel Blob Storage
 * @param {string} url - Image URL
 * @returns {boolean}
 */
export const isVercelBlobUrl = (url) => {
  return url && url.includes('blob.vercel-storage.com');
};

/**
 * Check if URL is a local development URL
 * @param {string} url - Image URL
 * @returns {boolean}
 */
export const isLocalUrl = (url) => {
  return url && url.startsWith('/uploads/');
};

/**
 * Generate responsive image URLs
 * Since images are pre-optimized on upload, we return the URL as-is
 * @param {string} url - Original image URL
 * @returns {object} - Object with src (no srcSet for pre-optimized images)
 */
export const generateResponsiveImageUrls = (url) => {
  if (!url) return { src: '', srcSet: '', sizes: '' };

  // Return URL as-is - images are pre-optimized on the server
  // No query parameters needed since Vercel Blob doesn't support them
  return {
    src: url,
    srcSet: '',
    sizes: ''
  };
};

/**
 * Generate srcset for avatar images
 * Avatars are pre-optimized to max 400px on upload
 * @param {string} url - Original image URL
 * @returns {object}
 */
export const generateAvatarSrcSet = (url) => {
  if (!url) return { src: '', srcSet: '', sizes: '' };

  // Return URL as-is - avatars are pre-optimized on the server
  return { src: url, srcSet: '', sizes: '' };
};

/**
 * Generate srcset for thumbnail images
 * Thumbnails are pre-generated at 400px on upload
 * @param {string} url - Original image URL
 * @returns {object}
 */
export const generateThumbnailSrcSet = (url) => {
  if (!url) return { src: '', srcSet: '', sizes: '' };

  // Return URL as-is - thumbnails are pre-generated on the server
  return { src: url, srcSet: '', sizes: '' };
};

/**
 * Get optimal quality parameter
 * Not applicable for Vercel Blob (no on-the-fly transformation)
 * @param {string} url - Image URL
 * @param {number} quality - Quality from 1-100 (ignored)
 * @returns {string}
 */
export const addQualityParam = (url, quality = 80) => {
  // Vercel Blob doesn't support quality parameters
  // Images are pre-compressed on upload
  return url || '';
};

/**
 * Preload critical images
 * @param {string} url - Image URL
 */
export const preloadImage = (url) => {
  if (!url) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  document.head.appendChild(link);
};

/**
 * Check if image format is supported
 * @param {string} format - Image format (webp, avif, etc.)
 * @returns {Promise<boolean>}
 */
export const supportsImageFormat = async (format) => {
  const testImages = {
    webp: 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=',
    avif: 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
  };

  if (!testImages[format]) return false;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = testImages[format];
  });
};

/**
 * Get best image format based on browser support
 * Note: Our server outputs JPEG for all optimized images
 * @returns {Promise<string>} - 'avif', 'webp', or 'jpg'
 */
export const getBestImageFormat = async () => {
  if (await supportsImageFormat('avif')) return 'avif';
  if (await supportsImageFormat('webp')) return 'webp';
  return 'jpg';
};
